import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateManyInstitutionalRulesetsMutation } from "store/queries/institutionalRuleset";

import {
  IInstitutionalRulesetFormData,
  INITIAL_INSTITUTIONAL_FORM_DATA,
  InstitutionalRulesetForm,
} from "./components/InstitutionalRulesetForm";

export function CreateInstitutionalRulesetPage() {
  useDocumentTitle("Create Institutional Ruleset");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createManyMutation, { isLoading }] =
    useCreateManyInstitutionalRulesetsMutation();
  // CLE-2597 — the contract view's "Configure" link on the engine-default row lands
  // here with the pair already known; prefilling it means the user isn't asked to
  // re-pick the facility and payer they just came from.
  const [searchParams] = useSearchParams();
  const prefillProviderId = searchParams.get("providerId");
  const prefillPayerId = Number(searchParams.get("payerId"));
  const [formData, setFormData] = useState<IInstitutionalRulesetFormData>({
    ...INITIAL_INSTITUTIONAL_FORM_DATA,
    providerIds: prefillProviderId ? [prefillProviderId] : [],
    payerIds:
      Number.isFinite(prefillPayerId) && prefillPayerId > 0
        ? [prefillPayerId]
        : [],
  });

  const handleSubmit = async () => {
    if (!formData.providerIds.length || !formData.payerIds.length) return;
    setIsLoading(true);
    try {
      const created = await createManyMutation({
        providerIds: formData.providerIds,
        payerIds: formData.payerIds,
        primarySelectionMethod: formData.primarySelectionMethod,
        ncciApplies: formData.ncciApplies,
        mueApplies: formData.mueApplies,
      }).unwrap();
      const count = created.data?.length ?? 0;
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: `${count} institutional ruleset${
          count === 1 ? "" : "s"
        } created successfully.`,
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to create institutional rulesets",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <InstitutionalRulesetForm
      title="Create Institutional Rulesets"
      submitLabel="Create Rulesets"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      multiScope
    />
  );
}
